import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import { checkCurrentRaceEntriesTableTab } from "pages/PreRaceScreeningPage/helper";
import { EPreRacesTableTab } from "pages/PreRaceScreeningPage/types";
import { EEventType, IExamsTableRowData, TFlagPriority } from "./types";

export enum EFlagPriority {
  HIGHEST = "highest",
  MEDIUM = "medium",
  LOWEST = "lowest",
  NONE = "none"
}

export const flagLevels = {
  [EFlagPriority.NONE]: "#7f7f7f",
  [EFlagPriority.LOWEST]: "#e3be00",
  [EFlagPriority.MEDIUM]: "#d82f44",
  [EFlagPriority.HIGHEST]: "#9100ff"
};

export const NO_TRAINER_TITLE = "NO TRAINER" as const;

export const getFlagPriority = (priority: TFlagPriority): EFlagPriority => {
  switch (priority) {
    case racehorse360.WorkoutRequestFlagPriority
      .WORKOUT_REQUEST_FLAG_PRIORITY_LOWER: {
      return EFlagPriority.LOWEST;
    }
    case racehorse360.UpcomingRaceEntryFlagPriority
      .UPCOMING_RACE_ENTRY_FLAG_PRIORITY_LOWER: {
      return EFlagPriority.LOWEST;
    }
    case racehorse360.WorkoutRequestFlagPriority
      .WORKOUT_REQUEST_FLAG_PRIORITY_MEDIUM: {
      return EFlagPriority.MEDIUM;
    }
    case racehorse360.UpcomingRaceEntryFlagPriority
      .UPCOMING_RACE_ENTRY_FLAG_PRIORITY_MEDIUM: {
      return EFlagPriority.MEDIUM;
    }
    case racehorse360.WorkoutRequestFlagPriority
      .WORKOUT_REQUEST_FLAG_PRIORITY_HIGHEST: {
      return EFlagPriority.HIGHEST;
    }
    case racehorse360.UpcomingRaceEntryFlagPriority
      .UPCOMING_RACE_ENTRY_FLAG_PRIORITY_HIGHEST: {
      return EFlagPriority.HIGHEST;
    }
    default: {
      return EFlagPriority.NONE;
    }
  }
};

export const examsRowDataMapper = (
  entity: racehorse360.IWorkoutRequest | racehorse360.IUpcomingRaceEntry,
  selectedTabValue?: EPreRacesTableTab
): IExamsTableRowData => {
  if (entity instanceof racehorse360.WorkoutRequest) {
    return {
      eventId: entity.id,
      eventType: EEventType.WORKOUT_REQUEST,
      eventDate: entity.date,
      eventCreationDate: entity.createdOn,
      eventStatus: entity.status,
      eventFlag: entity.flag,
      eventFacility: entity.facility,
      eventRejectionNote: entity.rejectionNote,
      hasEventComment: entity.hasComment,
      hasEventRacingSecretaryComment: entity.hasRacingSecretaryComment,
      pastEventsCount: entity.pastWorkoutRequestsCount,
      examId: entity.workoutExam?.id,
      examStatus: entity.workoutExam?.status,
      examCreatedBy: entity.workoutExam?.createdBy,
      examType: entity.workoutExam?.type,
      examAssignedUser: entity.workoutExam?.assignedUser,
      examNote: entity.examReasonNote,
      horse: entity.horse,
      highSpeedFurlongs: entity.highSpeedFurlongs
    };
  } else if (entity instanceof racehorse360.UpcomingRaceEntry) {
    const {
      isEntryExamsTab,
      isEntryResultsTab,
      isRaceDayTab,
      isRaceDayResultsTab
    } = checkCurrentRaceEntriesTableTab(selectedTabValue);

    let currentExamId: string;
    let currentExamStatus: racehorse360.HorseExamStatus;
    let currentExamAssignedUser: racehorse360.IUser;
    let currentExamType: racehorse360.HorseExamType;
    let currentExamResult: racehorse360.HorseExamResult;
    let currentExamCompletedBy: racehorse360.IUser;
    let currentExamCompletedOn: string;
    if (isEntryExamsTab || isEntryResultsTab) {
      currentExamId = entity.entryExam?.id;
      currentExamStatus = entity.entryExam?.status;
      currentExamAssignedUser = entity.entryExam?.assignedUser;
      currentExamType = entity.entryExam?.type;
      currentExamResult = entity.entryExam?.result;
      currentExamCompletedBy = entity.entryExam?.updatedBy;
      currentExamCompletedOn = entity.entryExam?.completedOn;
    } else if (isRaceDayTab || isRaceDayResultsTab) {
      currentExamId = entity.raceDayExam?.id;
      currentExamStatus = entity.raceDayExam?.status;
      currentExamAssignedUser = entity.raceDayExam?.assignedUser;
      currentExamType = entity.raceDayExam?.type;
      currentExamResult = entity.raceDayExam?.result;
      currentExamCompletedBy = entity.raceDayExam?.updatedBy;
      currentExamCompletedOn = entity.raceDayExam?.completedOn;
    }

    return {
      eventId: entity.id,
      eventType: EEventType.PRE_RACE,
      eventDate: entity.raceDate,
      eventNumber: entity.raceNumber,
      eventPosition: entity.racePosition,
      eventCreationDate: entity.createdOn,
      eventStatus: entity.status,
      eventFlag: entity.flag,
      eventFacility: entity.facility,
      examId: currentExamId,
      examStatus: currentExamStatus,
      examNote: entity.entryExamNote,
      examType: currentExamType,
      examResult: currentExamResult,
      examAssignedUser: currentExamAssignedUser,
      examCompletedBy: currentExamCompletedBy,
      examCompletedOn: currentExamCompletedOn,
      raceDayExamConclusion: entity.raceDayExam?.conclusion?.description,
      horse: entity.horse,
      highSpeedFurlongs: entity.highSpeedFurlongs,
      upcomingRace: entity.upcomingRace,
      claimChange: entity.claimChange,
      classChange: entity.classChange
    };
  } else {
    throw new Error(
      `Incorrect entity passed as parameter. It should be ${racehorse360.UpcomingRaceEntry.name}
      or ${racehorse360.WorkoutRequest.name}`
    );
  }
};

export const filterVisibleCheckedWorkoutIds = (
  items: IExamsTableRowData[],
  allCheckedIds: string[]
): string[] => {
  if (!items?.length || !allCheckedIds?.length) {
    return [];
  }

  return items
    .filter(item => allCheckedIds.includes(item.eventId))
    .map(item => item.eventId);
};
